import { useState, useEffect } from 'react';
import { API } from '@/lib/axios';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, Award, Building2, Mail, Calendar, HardHat, Shield } from 'lucide-react';

const getBackendURL = () => {
  const currentDomain = window.location.hostname;
  if (currentDomain.includes('.emergent')) {
    return `https://${currentDomain}`;
  }
  return API.replace(/\/api$/, '');
};

const VerifyWorker = () => {
  const { token } = useParams();
  const [worker, setWorker] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [verifiedAt, setVerifiedAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyWorker = async () => {
      try {
        const response = await axios.get(`${getBackendURL()}/api/public/verify/${token}`);
        setWorker(response.data.worker);
        setCertificates(response.data.certificates);
        setVerifiedAt(response.data.verifiedAt);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to verify worker credentials');
      } finally {
        setLoading(false);
      }
    };

    verifyWorker();
  }, [token]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Verifying credentials...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <CardTitle className="text-center text-red-900">Verification Failed</CardTitle>
            <CardDescription className="text-center">{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
              <p className="font-medium mb-2">This could happen if:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>The verification link is invalid or expired</li>
                <li>The worker account has been deleted</li>
                <li>The QR code was not generated properly</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const validCertificates = certificates.filter(cert => cert.status === 'valid');
  const expiredCertificates = certificates.filter(cert => cert.status === 'expired');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header with verified badge */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center">
            <div className="bg-green-100 p-4 rounded-full">
              <Shield className="w-12 h-12 text-green-600" />
            </div>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center justify-center gap-2">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
              Verified Training Credentials
            </h1>
            <p className="text-gray-600 mt-2">
              This worker's training certifications have been verified
            </p>
          </div>
        </div>

        {/* Worker Information */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50">
            <div className="flex items-center gap-3">
              <HardHat className="w-6 h-6 text-orange-600" />
              <CardTitle>Worker Information</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Award className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Full Name</p>
                    <p className="text-lg font-semibold text-gray-900">{worker.name}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="text-base text-gray-900">{worker.email}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Company</p>
                    <p className="text-base font-medium text-gray-900">{worker.companyName}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Verified On</p>
                    <p className="text-base text-gray-900">{formatDate(verifiedAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Valid Certificates */}
        {validCertificates.length > 0 && (
          <Card>
            <CardHeader className="bg-green-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                  <CardTitle className="text-green-900">Valid Certifications</CardTitle>
                </div>
                <Badge className="bg-green-600">{validCertificates.length} Active</Badge>
              </div>
              <CardDescription>Current and active training certifications</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {validCertificates.map((cert) => (
                  <div 
                    key={cert.certificateId}
                    className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg"
                  >
                    <div className="space-y-1">
                      <h4 className="font-semibold text-gray-900">{cert.courseName}</h4>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>Issued: {formatDate(cert.issueDate)}</span>
                        <span>•</span>
                        <span>Expires: {formatDate(cert.expiryDate)}</span>
                      </div>
                    </div>
                    <Badge variant="default" className="bg-green-600">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Valid
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Expired Certificates */}
        {expiredCertificates.length > 0 && (
          <Card>
            <CardHeader className="bg-orange-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <XCircle className="w-6 h-6 text-orange-600" />
                  <CardTitle className="text-orange-900">Expired Certifications</CardTitle>
                </div>
                <Badge variant="destructive">{expiredCertificates.length} Expired</Badge>
              </div>
              <CardDescription>These certifications need to be renewed</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {expiredCertificates.map((cert) => (
                  <div 
                    key={cert.certificateId}
                    className="flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-lg"
                  >
                    <div className="space-y-1">
                      <h4 className="font-semibold text-gray-900">{cert.courseName}</h4>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>Issued: {formatDate(cert.issueDate)}</span>
                        <span>•</span>
                        <span>Expired: {formatDate(cert.expiryDate)}</span>
                      </div>
                    </div>
                    <Badge variant="destructive">
                      <XCircle className="w-3 h-3 mr-1" />
                      Expired
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* No Certificates */}
        {certificates.length === 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8 text-gray-600">
                <Award className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No training certifications on record</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer Info */}
        <div className="text-center text-sm text-gray-600 py-4">
          <p>This verification was generated via SafetyMind AI Training Platform</p>
          <p className="mt-1">All information is current as of {formatDate(verifiedAt)}</p>
        </div>
      </div>
    </div>
  );
};

export default VerifyWorker;
